// frontend/src/pages/lead/[id].jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/auth';
import { getLead, purchaseLead, updateLead } from 'api';
import { formatCurrency } from '../../utils';
import NavBar from '../../components/NavBar';

const LeadDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [originalLead, setOriginalLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState({});

  useEffect(() => {
    const fetchLeadDetails = async () => {
      setLoading(true);
      try {
        const result = await getLead(id);
        if (result.success) {
          setLead(result.data);
          setOriginalLead(result.data);
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError('Failed to load lead details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeadDetails();
  }, [id]);

  const handlePurchase = async () => {
    setPurchaseLoading(true);
    try {
      const result = await purchaseLead(id);
      if (result.success) {
        setLead(prevLead => ({ ...prevLead, isPurchased: true, purchasedAt: new Date().toISOString() }));
        setOriginalLead(prevLead => ({ ...prevLead, isPurchased: true, purchasedAt: new Date().toISOString() }));
        setShowPurchaseModal(false);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to purchase lead. Please try again.');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleEditChange = (field, value) => {
    setLead(prevLead => ({ ...prevLead, [field]: value }));
    if (originalLead[field] !== value) {
      setEditedFields(prev => ({ ...prev, [field]: true }));
    } else {
      setEditedFields(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSaveEdits = async () => {
    try {
      // Include is_manually_edited: true in the update payload
      const updatedLead = { ...lead, is_manually_edited: true };
      const result = await updateLead(id, updatedLead);
      if (result.success) {
        setOriginalLead(updatedLead); // Update original data with the new values
        setIsEditing(false);
        setError('');
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to save changes. Please try again.');
    }
  };

  const renderField = (label, field, value) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {editedFields[field] && <span className="ml-2 text-xs text-blue-600">[Edited]</span>}
      </label>
      {isEditing ? (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => handleEditChange(field, e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        />
      ) : (
        <p className="mt-1 text-sm text-gray-900">{value || 'N/A'}</p>
      )}
    </div>
  );

  return (
    <>
      <NavBar user={user} onLogout={() => window.location.href = '/'} isAdmin={false} />
      <div className="lead-detail-page p-6">
        <div className="lead-detail-header mb-6 flex justify-between items-center">
          <button onClick={() => navigate(-1)} className="btn btn-secondary">Back to Dashboard</button>
          <div className="flex gap-2">
            {!lead?.isPurchased && (
              <button onClick={() => setShowPurchaseModal(true)} className="btn btn-primary">
                Purchase Lead - {formatCurrency(lead?.price || 50)}
              </button>
            )}
            {lead && (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`btn ${isEditing ? 'btn-secondary' : 'btn-primary'}`}
              >
                {isEditing ? 'Cancel' : 'Edit Lead'}
              </button>
            )}
            {isEditing && (
              <button onClick={handleSaveEdits} className="btn btn-primary">
                Save Changes
              </button>
            )}
          </div>
        </div>

        {error && <div className="error-alert">{error}</div>}
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <svg className="animate-spin h-10 w-10 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : lead ? (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Lead Details</h2>
            {/* Lead Details */}
            {renderField('Full Name', 'full_name', lead.full_name)}
            {renderField('Email', 'email', lead.email)}
            {renderField('Phone', 'phone', lead.phone)}
            {renderField('Zip Code', 'zip_code', lead.zip_code)}
            {renderField('Age', 'age', lead.age)}

            {/* Investment Details */}
            {renderField('Investment Amount', 'investment_amount', lead.investment_amount)}
            {renderField('Annuity Type', 'annuity_type', lead.annuity_type)}
            {renderField('Annuity Type Preference', 'annuity_type_preference', lead.annuity_type_preference)}
            {renderField('Term Length', 'term_length', lead.term_length)}

            {/* Income & Retirement */}
            {renderField('Income Start Time', 'income_start_time', lead.income_start_time)}
            {renderField('Retirement Timeline', 'retirement_timeline', lead.retirement_timeline)}

            {/* Annuity Preferences */}
            {renderField('Primary Goal', 'primary_goal', lead.primary_goal)}
            {renderField('Financial Comfort Level', 'financial_comfort_level', lead.financial_comfort_level)}
            {renderField('Contact Preference', 'contact_preference', lead.contact_preference)}

            {/* Marketing & Tracking */}
            {renderField('Campaign ID', 'campaign_id', lead.campaign_id)}
            {renderField('Ad Group ID', 'ad_group_id', lead.ad_group_id)}
            {renderField('Ad ID', 'ad_id', lead.ad_id)}
            {renderField('UTM Medium', 'utm_medium', lead.utm_medium)}
            {renderField('UTM Source', 'utm_source', lead.utm_source)}
            {renderField('UTM Campaign', 'utm_campaign', lead.utm_campaign)}
            {renderField('UTM Content', 'utm_content', lead.utm_content)}
            {renderField('UTM Term', 'utm_term', lead.utm_term)}

            {/* Additional Fields */}
            {renderField('Status', 'status', lead.status)}
            {renderField('Price', 'price', formatCurrency(lead.price))}
            {renderField('Created At', 'created_at', new Date(lead.created_at).toLocaleString())}
            {renderField('Manually Edited', 'is_manually_edited', lead.is_manually_edited ? 'Yes' : 'No')}

            {lead.isPurchased && (
              <div className="purchased-info mt-6 p-4 bg-green-50 rounded-md">
                <h3 className="text-lg font-semibold text-green-800">You've purchased this lead!</h3>
                <p className="text-green-700">Complete contact information is now available above.</p>
                <p className="text-green-700">Purchase date: {new Date(lead.purchasedAt).toLocaleString()}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="not-found text-center p-6 text-gray-600">Lead not found.</div>
        )}

        {showPurchaseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Confirm Purchase</h2>
              <p className="text-gray-600 mb-4">
                You are about to purchase this lead for {formatCurrency(lead.price || 50)}. Proceed with payment?
              </p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowPurchaseModal(false)} className="btn btn-secondary">Cancel</button>
                <button onClick={handlePurchase} disabled={purchaseLoading} className="btn btn-primary">
                  {purchaseLoading ? 'Processing...' : 'Confirm Purchase'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LeadDetailPage;